import React, { Context } from 'react';
import { MessageType } from 'ia-react-core/~/interfaces/MessageType';

import { MessageMap } from 'ia-react-core/~/interfaces/MessageMap';
import Translation from '../../../services/translation/Translation';
import addTranslation from '../../../utils/translation/addTranslation';

type Watcher<Value, > = (map: MessageMap<Value>) => void;

addTranslation('fr', 'Core/ValidationContext', { message: 'Ce champ est invalide' });
addTranslation('en', 'Core/ValidationContext', { message: 'This field is invalid' });

let id = 0;

const getNewValidationMessageId = () => {
  id += 1;
  return id;
};

const messages: {
  validationMessageId: number;
  type: string;
  message: MessageType<any>;
}[] = [
  {
    validationMessageId: getNewValidationMessageId(),
    type: 'default',
    message: () => Translation.t('Core/ValidationContext:message'),
  },
];

let messageMap: MessageMap;
const watchers: Watcher<any>[] = [];

const registerMessage = <Value, >(validationMessageId: number, type: string, message: MessageType<Value>) => {
  messages.push({ validationMessageId, type, message });
  messageMap = undefined;
  watchers.forEach((watcher) => watcher(getMessages()));
};

const unregisterMessage = (validationMessageId: number) => {
  const index = messages.findIndex((message) => message.validationMessageId === validationMessageId);
  messages.splice(index, 1);
  messageMap = undefined;
  watchers.forEach((watcher) => watcher(getMessages()));
};

const getMessages = <Value, >() => {
  if (messageMap) {
    return messageMap as unknown as MessageMap<Value>;
  }

  messageMap = messages.reduce<MessageMap<Value>>((currentMessages, { type, message }) => {
    currentMessages[type] = message;
    return currentMessages;
  }, {}) as MessageMap<any>;

  return messageMap as unknown as MessageMap<Value>;
};

const addGlobalMessagesListener = <Value, >(fn: Watcher<Value>) => {
  watchers.push(fn);
  fn(getMessages<Value>());
};

const removeGlobalMessagesListener = <Value, >(fn: Watcher<Value>) => {
  const index = watchers.indexOf(fn);
  watchers.splice(index, 1);
};

interface ValidationContextProps {
  messages?: MessageMap;
  registerMessage: typeof registerMessage;
  unregisterMessage: typeof unregisterMessage;
}

interface ValidationContextStatics {
  getNewValidationMessageId: typeof getNewValidationMessageId;
  registerMessage: typeof registerMessage;
  unregisterMessage: typeof unregisterMessage;
  getMessages: typeof getMessages;
  addGlobalMessagesListener: typeof addGlobalMessagesListener;
  removeGlobalMessagesListener: typeof removeGlobalMessagesListener;
}

const ValidationContext: Context<ValidationContextProps> & ValidationContextStatics = Object.assign(
  React.createContext({
    registerMessage,
    unregisterMessage,
  }),
  {
    getNewValidationMessageId,
    registerMessage,
    unregisterMessage,
    getMessages,
    addGlobalMessagesListener,
    removeGlobalMessagesListener,
  },
);

export default ValidationContext;
